import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import {
    Add as AddIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    DeleteOutline as DeleteIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    RestartAlt as RestartAltIcon,
    BusinessCenter as BusinessCenterIcon,
    Link as LinkIcon,
    LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { createCustomHeaderField } from '../../../Utils/DocumentVersion/documentDesignVersions';
import { headerFieldJoinsPrevious } from '../../../Utils/DocumentVersion/documentSettingsDefaults';
import styles from './DocumentSettingsDialog.module.scss';

const FIELD_TITLE_KEYS = {
    businessName: ['HeaderFieldBusinessName', 'שם העסק'],
    taxId: ['HeaderFieldTaxId', 'ח"פ / ע"מ'],
    address: ['HeaderFieldAddress', 'כתובת'],
    phone: ['HeaderFieldPhone', 'טלפון 1'],
    phone2: ['HeaderFieldPhone2', 'טלפון 2'],
    email: ['HeaderFieldEmail', 'אימייל'],
    website: ['HeaderFieldWebsite', 'אתר'],
    custom: ['HeaderFieldCustom', 'שדה מותאם'],
};

const isPrinted = (field) => field.visible !== false && String(field.value ?? '').trim();

/**
 * Edits the business header of the active design version. Values are stored on the version
 * only; the client/business record is never modified. Fields linked with `joinPrevious`
 * print on one line in the document header.
 */
const HeaderDetailsEditor = ({ t, direction, headerDetails, onChange, onResetFromBusiness }) => {
    const tr = (key, fallback) => {
        const value = t(key);
        return value && value !== key ? value : fallback;
    };
    const fields = headerDetails?.fields || [];
    const textAlign = direction === 'ltr' ? 'left' : 'right';

    const update = (nextFields) => onChange({ ...(headerDetails || {}), fields: nextFields });

    const updateField = (id, patch) => update(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));

    const joinsPrevious = (index) => headerFieldJoinsPrevious(fields[index], fields[index - 1]);

    const canJoin = (index) => index > 0
        && fields[index].type !== 'businessName'
        && fields[index - 1].type !== 'businessName';

    // The row layout stays with the position: moving swaps the fields, not the line grouping.
    const moveField = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= fields.length) return;
        const next = [...fields];
        const joinAtIndex = joinsPrevious(index);
        const joinAtTarget = joinsPrevious(target);
        next[index] = { ...fields[target], joinPrevious: joinAtIndex };
        next[target] = { ...fields[index], joinPrevious: joinAtTarget };
        update(next);
    };

    const removeField = (id) => {
        const index = fields.findIndex((f) => f.id === id);
        if (index < 0) return;
        const next = fields.filter((f) => f.id !== id);
        // Removing the first field of a shared line keeps the rest of that line together on its own
        if (!joinsPrevious(index) && joinsPrevious(index + 1)) {
            next[index] = { ...next[index], joinPrevious: false };
        }
        update(next);
    };

    const toggleJoin = (index) => updateField(fields[index].id, { joinPrevious: !joinsPrevious(index) });

    const addField = () => update([...fields, createCustomHeaderField()]);

    const groups = [];
    fields.forEach((field, index) => {
        if (index > 0 && joinsPrevious(index)) {
            groups[groups.length - 1].push({ field, index });
        } else {
            groups.push([{ field, index }]);
        }
    });

    const renderField = ({ field, index }) => {
        const [titleKey, titleFallback] = FIELD_TITLE_KEYS[field.type] || FIELD_TITLE_KEYS.custom;
        const isName = field.type === 'businessName';
        const joined = joinsPrevious(index);
        return (
            <div
                key={field.id}
                className={`${styles.headerFieldRow}${field.visible ? '' : ` ${styles.headerFieldHidden}`}`}
            >
                <div className={styles.headerFieldType}>{tr(titleKey, titleFallback)}</div>
                <div className={styles.headerFieldInputs}>
                    {!isName && (
                        <input
                            className={`${styles.headerFieldInput} ${styles.headerFieldLabelInput}`}
                            value={field.label}
                            placeholder={tr('HeaderFieldLabelPlaceholder', 'תווית')}
                            onChange={(e) => updateField(field.id, { label: e.target.value })}
                            style={{ textAlign }}
                            dir={direction}
                        />
                    )}
                    <input
                        className={styles.headerFieldInput}
                        value={field.value}
                        placeholder={tr('HeaderFieldValuePlaceholder', 'ערך')}
                        onChange={(e) => updateField(field.id, { value: e.target.value })}
                        style={{ textAlign }}
                        dir={direction}
                    />
                </div>
                <div className={styles.headerFieldActions}>
                    <Tooltip
                        title={joined
                            ? tr('HeaderFieldSplitRow', 'הצג בשורה נפרדת')
                            : tr('HeaderFieldJoinRow', 'הצג באותה שורה עם השדה הקודם')}
                    >
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => toggleJoin(index)}
                                disabled={!canJoin(index)}
                                className={`${styles.versionActionBtn}${joined ? ` ${styles.headerFieldJoinActive}` : ''}`}
                                aria-pressed={joined}
                            >
                                {joined ? <LinkOffIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={field.visible ? tr('HideField', 'הסתר') : tr('ShowField', 'הצג')}>
                        <IconButton
                            size="small"
                            onClick={() => updateField(field.id, { visible: !field.visible })}
                            className={styles.versionActionBtn}
                        >
                            {field.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        size="small"
                        onClick={() => moveField(index, -1)}
                        disabled={index === 0}
                        className={styles.versionActionBtn}
                        aria-label={tr('MoveUp', 'הזז למעלה')}
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => moveField(index, 1)}
                        disabled={index === fields.length - 1}
                        className={styles.versionActionBtn}
                        aria-label={tr('MoveDown', 'הזז למטה')}
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <Tooltip title={tr('RemoveField', 'הסר שדה')}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => removeField(field.id)}
                                disabled={isName}
                                className={`${styles.versionActionBtn} ${styles.versionDeleteBtn}`}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </div>
        );
    };

    const renderRowPreview = (group) => {
        const printed = group.map(({ field }) => field).filter(isPrinted);
        if (printed.length === 0) return tr('HeaderSharedRowEmpty', 'השורה לא תוצג (אין ערכים)');
        return printed.map((f) => (String(f.label ?? '').trim()
            ? `${String(f.label).trim()}: ${String(f.value).trim()}`
            : String(f.value).trim())).join(', ');
    };

    return (
        <div className={styles.sectionBlock} dir={direction}>
            <div className={styles.versionsHeader}>
                <div className={styles.sectionHeading}>
                    <BusinessCenterIcon className={styles.sectionHeadingIcon} />
                    {tr('HeaderBusinessDetails', 'פרטי העסק בכותרת המסמך')}
                </div>
                <div className={styles.versionsHeaderActions}>
                    <Tooltip title={tr('ResetHeaderFromBusiness', 'טען מחדש מפרטי העסק')}>
                        <IconButton size="small" onClick={onResetFromBusiness} className={styles.versionActionBtn}>
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <button type="button" className={styles.versionAddBtn} onClick={addField}>
                        <AddIcon fontSize="small" />
                        {tr('AddHeaderField', 'הוסף שדה')}
                    </button>
                </div>
            </div>
            <span className={styles.hint}>
                {tr('HeaderBusinessDetailsHint', 'הערכים נשמרים לפריסה ולגרסת העיצוב הנוכחיות בלבד ואינם משנים את פרטי העסק המקוריים.')}
            </span>
            <span className={styles.hint}>
                {tr('HeaderSharedRowHint', 'לחצו על סמל השרשרת כדי להציג שדה באותה שורה עם השדה שמעליו (למשל ח"פ וכתובת).')}
            </span>

            <div className={styles.headerFieldList}>
                {groups.map((group) => (group.length === 1
                    ? renderField(group[0])
                    : (
                        <div key={`row-${group[0].field.id}`} className={styles.headerFieldGroup}>
                            <div className={styles.headerFieldGroupTitle}>
                                <LinkIcon fontSize="inherit" />
                                <span>{tr('HeaderSharedRow', 'שורה משותפת')}</span>
                                <span className={styles.headerFieldGroupPreview} dir={direction}>
                                    {renderRowPreview(group)}
                                </span>
                            </div>
                            {group.map(renderField)}
                        </div>
                    )))}
            </div>
        </div>
    );
};

export default HeaderDetailsEditor;
